import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Wrench,
  FileText,
  Search,
  Filter
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function MaintenancesReportPage() {
  const { API } = useAuth();
  const [loading, setLoading] = useState(true);
  const [maintenances, setMaintenances] = useState([]);
  const [filteredMaintenances, setFilteredMaintenances] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  
  // Filters
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterMaintenances();
  }, [maintenances, dataInicial, dataFinal, selectedVehicle]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [maintResponse, vehiclesResponse] = await Promise.all([
        axios.get(`${API}/maintenances`, { withCredentials: true }),
        axios.get(`${API}/vehicles`, { withCredentials: true })
      ]);
      setMaintenances(maintResponse.data);
      setVehicles(vehiclesResponse.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filterMaintenances = () => {
    let result = [...maintenances];

    // Filter by vehicle
    if (selectedVehicle) {
      result = result.filter(m => m.vehicle_id === selectedVehicle);
    }

    // Filter by date range
    if (dataInicial) {
      result = result.filter(m => m.data_manutencao >= dataInicial);
    }
    if (dataFinal) {
      result = result.filter(m => m.data_manutencao <= dataFinal);
    }

    // Sort by date descending
    result.sort((a, b) => new Date(b.data_manutencao) - new Date(a.data_manutencao));

    setFilteredMaintenances(result);
  };

  const generateReport = () => {
    filterMaintenances();
    toast.success('Relatório gerado!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd/MM/yyyy', { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const totalCost = filteredMaintenances.reduce((acc, m) => acc + (m.valor || 0), 0);
  
  // Get selected vehicle info for display
  const selectedVehicleInfo = vehicles.find(v => v.vehicle_id === selectedVehicle);

  return (
    <div className="space-y-6" data-testid="maintenances-report-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800 font-[Manrope]">Relatório de Manutenções</h1>
        <p className="text-gray-500 text-sm">Filtre e analise os custos de manutenção da frota.</p>
      </div>

      {/* Filters Card */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Filtros do Relatório</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <Label className="text-sm text-gray-600">Data Inicial</Label>
              <Input
                type="date"
                value={dataInicial}
                onChange={(e) => setDataInicial(e.target.value)}
                data-testid="data-inicial-filter"
              />
            </div>
            
            <div>
              <Label className="text-sm text-gray-600">Data Final</Label>
              <Input
                type="date"
                value={dataFinal}
                onChange={(e) => setDataFinal(e.target.value)}
                data-testid="data-final-filter"
              />
            </div>
            
            <div>
              <Label className="text-sm text-gray-600">Veículo</Label>
              <Select value={selectedVehicle || 'all'} onValueChange={(v) => setSelectedVehicle(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="vehicle-filter">
                  <SelectValue placeholder="Todos os veículos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os veículos</SelectItem>
                  {vehicles.map(v => (
                    <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                      {v.vehicle_id} - {v.marca} {v.modelo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Button onClick={generateReport} className="w-full bg-blue-600 hover:bg-blue-700">
                <FileText className="w-4 h-4 mr-2" />
                Gerar Relatório
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Content */}
      <Card>
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-800 mb-4">Resultado do Relatório</h3>
          
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredMaintenances.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Wrench className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>Nenhuma manutenção encontrada para os filtros selecionados</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold">Veículo</TableHead>
                      <TableHead className="font-semibold">Data</TableHead>
                      <TableHead className="font-semibold">Tipo</TableHead>
                      <TableHead className="font-semibold">Descrição</TableHead>
                      <TableHead className="font-semibold">Mecânico</TableHead>
                      <TableHead className="font-semibold text-right">Custo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredMaintenances.map((maint) => (
                      <TableRow key={maint.maintenance_id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          <div>
                            <p className="font-semibold">{maint.vehicle_id}</p>
                            <p className="text-xs text-gray-500">{maint.veiculo_info}</p>
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(maint.data_manutencao)}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            maint.tipo === 'preventiva' ? 'bg-blue-100 text-blue-700' :
                            maint.tipo === 'corretiva' ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {maint.tipo ? maint.tipo.charAt(0).toUpperCase() + maint.tipo.slice(1) : 'Revisao'}
                          </span>
                        </TableCell>
                        <TableCell className="max-w-xs truncate" title={maint.descricao}>
                          {maint.descricao || '-'}
                        </TableCell>
                        <TableCell>{maint.responsavel || 'N/A'}</TableCell>
                        <TableCell className={`text-right font-semibold ${(maint.valor || 0) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                          {formatCurrency(maint.valor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Summary */}
              <div className="mt-6 flex justify-end">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-600">Total de Manutenções: <strong>{filteredMaintenances.length}</strong></p>
                  <p className="text-lg font-bold text-gray-800">
                    Custo Total: <span className="text-red-600">{formatCurrency(totalCost)}</span>
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
